// The notification-channel RULES, pure and framework-free (contract 22 §5 "service/
// channelValidation.ts", ORP-18886). Nothing here touches Vue, HTTP, i18n or the DOM: every
// function is a value in, a value out, so each rule is testable without mounting anything.
//
// Four of these are ports of legacy behaviour that disappears silently when it is dropped:
//
//   * `isValidWebhook` — the TEAMS clause (`NotificationUpdateModal.vue:173-178`). See
//     `WebhookRule` for why the contract's `webhookPrefix?: string` cannot hold it.
//   * `formatTrGsm` — v3's `formatTRGsm3322` (`composables/format.js:449-461`). v4 has no
//     equivalent and losing it turns a formatted chip back into raw digits with no error. It is
//     string arithmetic, not `Intl`, so it is legal in a service (module 04's ban is on `Intl`).
//     This is Turkish DATA handling, which survives the English-only cutover on purpose.
//   * `enableIntent` — the derived enable/disable (`NotificationUpdateModal.vue:190-196` →
//     `NotificationCard.vue:103-105`), which legacy expressed as a `{useValue, value}` pair
//     threaded through two components and two emits.
//   * `invalidRecipients` — see below. This function is why D1 cannot come back.
import { CHANNEL_CATALOG } from '../models/channel.types'
import type {
  ChannelAvailability,
  ChannelConfig,
  ChannelDescriptor,
  InvalidRecipientsDto,
  NotificationChannelDto,
  NotificationInformationDto,
} from '../models/channel.types'

import { ApiError } from '@/shared/http'

/* ── Reading the payload ────────────────────────────────────────────────────── */

/** Pull one channel's config out of the bare `{channels:{…}}` body. A channel the backend has
 *  never heard of reads as "not configured, not enabled" rather than throwing. */
export function channelConfig(
  payload: NotificationInformationDto | null | undefined,
  descriptor: ChannelDescriptor,
): ChannelConfig {
  const block: NotificationChannelDto | undefined = payload?.channels?.[descriptor.channel]
  const raw = block?.[descriptor.valuesKey]
  return {
    enabled: block?.enabled === true,
    // `.filter(Boolean)` is load-bearing, not defensive: legacy seeds webhook channels with a
    // `[null]` slot and persists whatever survives, so a stored list can contain empties.
    values: Array.isArray(raw) ? raw.filter((value): value is string => Boolean(value)) : [],
  }
}

/**
 * The whole payload → what 13/14/17 read: configured + enabled + recipients, per channel, always
 * the full `CHANNEL_CATALOG` and never a partial map.
 *
 * It is a function rather than a line inside `useNotificationChannels` because the seam derives
 * availability from two places — the pane's own reactive state and `loadFor(workspaceId)`, the
 * one-shot read for a workspace that is not the selected one. Two copies of this rule is exactly
 * how "configured" and "enabled" drift apart.
 */
export function channelAvailability(
  payload: NotificationInformationDto | null | undefined,
): ChannelAvailability[] {
  return CHANNEL_CATALOG.map((descriptor) => {
    const config = channelConfig(payload, descriptor)
    return {
      channel: descriptor.channel,
      configured: config.values.length > 0,
      enabled: config.enabled,
      recipients: config.values,
    }
  })
}

/* ── Webhook (slack, teams) ─────────────────────────────────────────────────── */

/**
 * BOTH clauses of the webhook rule (`NotificationUpdateModal.vue:173-178`): the URL must start
 * with the channel's prefix AND — for Teams, the only channel with a `mustContain` — contain
 * `webhook`. Dropping the second clause makes `https://example.com` a valid Teams webhook.
 */
export function isValidWebhook(descriptor: ChannelDescriptor, url: string | null): boolean {
  const rule = descriptor.webhook
  if (!rule) return true
  const value = url ?? ''
  if (!value.startsWith(rule.prefix)) return false
  return rule.mustContain === undefined || value.includes(rule.mustContain)
}

/* ── Phone (whatsapp) ───────────────────────────────────────────────────────── */

/** Legacy's minimum, verbatim (`:184`) — a length in CHARACTERS including the leading `+`. */
export const PHONE_MIN_LENGTH = 10

/** Why a number was refused — a CODE, never copy. The pane maps it to an i18n key (§13). */
export type PhoneRejection = 'format' | 'duplicate'

/**
 * `NotificationUpdateModal.vue:179-187`, rule for rule: starts `+`, digits after it, at least
 * {@link PHONE_MIN_LENGTH} characters, and not already in the list. Returns `null` when the value
 * is acceptable.
 *
 * Legacy folded the first three checks into one message; they stay one code here for the same
 * reason — three ways to say "that is not a phone number" is not three pieces of information.
 */
export function phoneRejection(
  value: string,
  existing: readonly string[] = [],
): PhoneRejection | null {
  const candidate = value.trim()
  if (!candidate) return null
  if (!/^\+\d+$/.test(candidate)) return 'format'
  if (candidate.length < PHONE_MIN_LENGTH) return 'format'
  if (existing.includes(candidate)) return 'duplicate'
  return null
}

/** `ChipInput.validate` shape — refuse-before-add, which is what phones need (ORP-19871). */
export function isValidPhone(value: string, existing: readonly string[] = []): boolean {
  return value.trim().length > 0 && phoneRejection(value, existing) === null
}

/**
 * v3's `formatTRGsm3322` (`composables/format.js:449-461`), ported verbatim: strip non-digits,
 * shed a `0090`/`90`/`0` national prefix, and group a Turkish mobile as `5XX XXX XX XX`. Anything
 * that is not a 10-digit number starting `5` is returned UNCHANGED — an international number keeps
 * its own shape rather than being mangled into a Turkish one.
 */
export function formatTrGsm(input: string | null | undefined): string {
  if (input === null || input === undefined) return ''

  let digits = String(input).replaceAll(/\D/gu, '')
  if (digits.startsWith('0090')) digits = digits.slice(4)
  else if (digits.startsWith('90')) digits = digits.slice(2)
  else if (digits.startsWith('0')) digits = digits.slice(1)

  if (digits.length !== 10 || !digits.startsWith('5')) return String(input)

  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`
}

/* ── The derived enable/disable ─────────────────────────────────────────────── */

/**
 * Saving recipients also flips the channel: 0 → n enables it, n → 0 disables it, and anything else
 * leaves it alone (`NotificationUpdateModal.vue:190-196`). `null` means "send no enable request",
 * which is why this returns a tri-state rather than a boolean.
 */
export function enableIntent(before: readonly string[], after: readonly string[]): boolean | null {
  const had = before.filter(Boolean).length > 0
  const has = after.filter(Boolean).length > 0
  if (!had && has) return true
  if (had && !has) return false
  return null
}

/* ── The `400` that used to be an XSS sink ──────────────────────────────────── */

/**
 * The addresses the backend refused, as DATA (contract §6.2 row 9, BE-22-03).
 *
 * D1 lived at `NotificationUpdateModal.vue:208-218`, which built `<li>${email}</li>` from this very
 * array and interpolated `data.message` into a `<p>`, then handed the string to swal as `text`.
 * Every server-controlled string reached the DOM as markup.
 *
 * The fix is structural, not a sanitiser: this returns `string[]`, `InvalidRecipientList.vue`
 * renders it with `v-for`/`{{ }}`, and there is no code path in the pane that concatenates a
 * server string into markup — so a future "sharpen" has nothing to re-break. `data.message` is
 * deliberately NOT surfaced: it is un-localised backend English (§13), and the list says more.
 */
export function invalidRecipients(cause: unknown): string[] {
  const payload = ApiError.isApiError(cause) ? cause.payload : cause
  if (!payload || typeof payload !== 'object') return []
  const { invalid_emails: invalid } = payload as InvalidRecipientsDto
  if (!Array.isArray(invalid)) return []
  return invalid.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0)
}

/* ── Write feedback ─────────────────────────────────────────────────────────── */

/**
 * A failed write → the i18n key that renders INLINE next to the control (contract §12/§14).
 *
 * These are `useBasicsWrite`'s three rules, deliberately kept identical: a 403 says so on the row
 * and is never a toast; a canceled request (navigation, unmount, workspace switch) is silent; every
 * other failure gets one localised sentence — the backend's own `message` is never rendered (§13).
 *
 * It is a pure function rather than a second `useBasicsWrite`: that composable's source shape is
 * `{ save → {refreshFailed} | null, pending, error }`, and neither notification write site can fill
 * it honestly — the channel re-read is the PANE's single `refresh()`, not a per-write re-hydrate, so
 * `refreshFailed` would be a constant `false` at both call sites. The RULE is what mattered, so the
 * rule is what is shared.
 */
export function writeErrorKey(error: ApiError | null): string | null {
  if (!error || error.kind === 'canceled') return null
  return error.status === 403
    ? 'workspaceSettings.error.forbidden'
    : 'workspaceSettings.notifications.saveFailed'
}
